import type { Attachment } from '@core/attachments-types';
import { AutomatedActionOffer, EmailSenderKindEnum } from '@core/automated-actions-types';
import { OfferStatus } from '@core/offers-types';
import { IconName } from '@core/user-interface-legacy';
import { Visibility } from '@core/visibility-types';

export enum AutomationActionType {
  // CANDIDATE
  CandidateSendEmail = 'candidate_send_email',
  CandidateAddTags = 'candidate_add_tags',
  CandidateAddNote = 'candidate_add_note',
  CandidateAddTask = 'candidate_add_task',
  CandidateEvaluationRequest = 'candidate_request_evaluation',
  CandidateAddFollowers = 'candidate_add_followers',
  CandidateAssignTo = 'candidate_assign_to_job',
  CandidateUnassignFromJob = 'candidate_remove_from_job',
  CandidateAddSourceTags = 'candidate_add_source_tags',
  CandidateCancelEvaluationRequests = 'candidate_delete_evaluation_requests',
  CandidateMoveToTheNextStage = 'candidate_move_to_the_next_stage',
  // JOB
  JobChangeStatus = 'offer_change_status',
  JobSubmitToJobBoards = 'offer_submit_to_job_boards',
  JobAddTeamMembers = 'offer_add_team_members',
  JobAddTags = 'offer_add_tags',
  JobAddNote = 'offer_add_note',
}

export type AssignToReferences = {
  offers: AutomatedActionOffer[];
  talentPools: AutomatedActionOffer[];
};
export type SendEmailReferences = {
  attachments: Attachment[];
};

export type AutomationAction = { iconName: IconName; name: string } & (
  | SendEmailAction
  | AddTagsAction
  | AddSourceTagsAction
  | AddTaskAction
  | AddFollowersAction
  | AddNoteAction
  | RequestEvaluationAction
  | AssignAction
  | UnassignAction
  | SubmitToJobBoardsAction
  | JobChangeStatusAction
  | AddTeamMembersAction
  | CancelEvaluationRequestsAction
  | MoveToTheNextStageAction
);

export type SendEmailAction = {
  type: AutomationActionType.CandidateSendEmail;
  data: SendEmailActionData;
  references?: SendEmailReferences;
};

export type AddTagsAction = {
  type: AutomationActionType.CandidateAddTags | AutomationActionType.JobAddTags;
  data: AddTagsActionData;
};

export type AddSourceTagsAction = {
  type: AutomationActionType.CandidateAddSourceTags;
  data: AddSourceTagsActionData;
};

export type AddTaskAction = {
  type: AutomationActionType.CandidateAddTask;
  data: AddTaskActionData;
};

export type AddFollowersAction = {
  type: AutomationActionType.CandidateAddFollowers;
  data: AddFollowersActionData;
};

export type AddNoteAction = {
  type: AutomationActionType.CandidateAddNote | AutomationActionType.JobAddNote;
  data: AddNoteActionData;
};

export type RequestEvaluationAction = {
  type: AutomationActionType.CandidateEvaluationRequest;
  data: RequestEvaluationActionData;
};

export type AssignAction = {
  type: AutomationActionType.CandidateAssignTo;
  data: AssignActionData;
};

export type UnassignAction = {
  type: AutomationActionType.CandidateUnassignFromJob;
  data: UnassignActionData;
};

export type SubmitToJobBoardsAction = {
  type: AutomationActionType.JobSubmitToJobBoards;
  data: null;
};

export type JobChangeStatusAction = {
  type: AutomationActionType.JobChangeStatus;
  data: JobChangeStatusData;
};

export type AddTeamMembersAction = {
  type: AutomationActionType.JobAddTeamMembers;
  data: AddTeamMembersActionData;
};

export type CancelEvaluationRequestsAction = {
  type: AutomationActionType.CandidateCancelEvaluationRequests;
  data: null;
};

export type MoveToTheNextStageAction = {
  type: AutomationActionType.CandidateMoveToTheNextStage;
  data: null;
};

export type AddTagsActionData = {
  tags: string[];
};

export type AddSourceTagsActionData = {
  sourceTags: string[];
};

export type AddTaskActionData = {
  adminIds: number[];
  dueDateType: 'relative' | 'absolute' | null;
  dueDateUnit: string | null;
  dueDateValue: string | null;
  title: string;
  timezone: string | null;
  adminIdsAddAdminPerformingAction: boolean;
  adminIdsAddHiringManager: boolean;
  adminIdsAddRecruiter: boolean;
};

export type AddFollowersActionData = {
  followerIds: number[];
  adminIdsAddRecruiter: boolean;
  adminIdsAddHiringManager: boolean;
};

export type AddTeamMembersActionData = {
  adminIds: number[];
};

export type AddNoteActionData = {
  body: string;
  bodyJson: object | null;
  bodyHtml: string;
  visibility: Visibility;
};

export type RequestEvaluationActionData = {
  interviewTemplateId: number | null;
  adminIds: number[];
  adminIdsAddHiringManager: boolean;
  adminIdsAddRecruiter: boolean;
};

export type AssignActionData = {
  offerIds: number[];
  talentPoolIds: number[];
  references?: AssignToReferences;
};

export type UnassignActionData = {
  offerIds: number[];
  talentPoolIds: number[];
  includeSelf: boolean;
  references?: AssignToReferences;
};

export type SendEmailActionData = {
  from: number | null;
  fromKind: EmailSenderKindEnum;
  toAdminIds: number[];
  toCandidate: boolean;
  toHiringManager: boolean;
  toRecruiter: boolean;
  ccAdminIds: number[];
  ccCandidate: boolean;
  ccHiringManager: boolean;
  ccRecruiter: boolean;
  bccAdminIds: number[];
  bccCandidate: boolean;
  bccHiringManager: boolean;
  bccRecruiter: boolean;
  subject: string;
  bodyHtml: string;
  signature: boolean;
  visibility: Visibility;
  sendAfterAtHour?: number | null;
  sendAfterAtMinute?: number | null;
  sendAfterDays?: number | null;
  sendAtTimezone?: string | null;
  attachmentIds: number[];
};

export type JobChangeStatusData = {
  status: Exclude<OfferStatus, OfferStatus.Draft>;
};
